import { UserPreferencesConfig } from 'softcat:config/user-preferences.ts';
import { SoftcatApiCatalogue } from 'softcat:types/softcat-api.ts';
import { SearchParameterUtils } from 'softcat:utils/search-parameters.ts';
import { SoftcatApiUtils } from 'softcat:utils/softcat-api.ts';

const create = async (data: Partial<SoftcatApiCatalogue>): Promise<SoftcatApiCatalogue> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.post<SoftcatApiCatalogue>(`/catalogues?${parameters}`, { body: { ...data, territory } });
};

type FindAllParameters = { limit?: number; orderBy?: string; page?: number; query?: string };

const buildFindAllSearchParameters = ({ limit = 10, orderBy = '', page = 0, query = '' }: FindAllParameters, nextToken?: string) => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);

    return new URLSearchParams(
        SearchParameterUtils.removeEmptySearchParameters({
            filter: `territory=${territory}${query ? `,name==${query}*` : ''}`,
            limit: `${limit}`,
            nextToken: nextToken ?? '',
            offset: nextToken === undefined ? `${page}` : '',
            orderBy: `${orderBy}`,
            territory: `${territory}`,
        }),
    );
};

const findAll = async (parameters: FindAllParameters = {}): Promise<SoftcatApiCatalogue[]> => {
    return SoftcatApiUtils.get<SoftcatApiCatalogue[]>(`/catalogues?${buildFindAllSearchParameters(parameters)}`);
};

const findAllWithNextToken = async ({ nextToken, ...parameters }: FindAllParameters & { nextToken?: string } = {}): Promise<{
    catalogues: SoftcatApiCatalogue[];
    nextToken: string | undefined;
}> => {
    const { data, headers } = await SoftcatApiUtils.getWithHeaders<SoftcatApiCatalogue[]>(`/catalogues?${buildFindAllSearchParameters(parameters, nextToken)}`);

    return { catalogues: data, nextToken: headers.get('X-Next-Token') ?? undefined };
};

const findOne = async ({ name, version }: { name: string; version: string }): Promise<SoftcatApiCatalogue> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.get<SoftcatApiCatalogue>(`/catalogues/${name}/${version}?${parameters}`);
};

const publish = async ({
    certificate,
    location,
    name,
    password,
    version,
}: {
    certificate: File;
    location: string;
    name: string;
    password: string;
    version: string;
}): Promise<SoftcatApiCatalogue> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    const form = new FormData();
    form.append('file', certificate);
    form.append('password', password);

    return SoftcatApiUtils.post<SoftcatApiCatalogue>(`/catalogues/action/publish/${name}/${version}/${location}?${parameters}`, { body: form });
};

const remove = async ({ name, version }: { name: string; version: string }): Promise<void> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.remove(`/catalogues/${name}/${version}?${parameters}`);
};

const update = async ({ data, name, version }: { data: Partial<SoftcatApiCatalogue>; name: string; version: string }): Promise<SoftcatApiCatalogue> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.put<SoftcatApiCatalogue>(`/catalogues/${name}/${version}?${parameters}`, { body: data });
};

export const CatalogueService = {
    create,
    findAll,
    findAllWithNextToken,
    findOne,
    publish,
    remove,
    update,
};
